import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../../../lib/api';
import { API_DOCS } from './TabFornitore.api';
import ApiInspector from '../../../../components/ApiInspector';
import BancaFormFields from '../../../../components/BancaFormFields';
import SottocontoCogeInfo from '../../../../components/SottocontoCogeInfo';

// ── Icons ─────────────────────────────────────────────────────────────────
const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

// ── Interfaces ────────────────────────────────────────────────────────────
interface CondizionePagamentoLite {
  id: number;
  nome: string;
  canale: string;
}

interface CodiceIvaLite {
  id: number;
  codice: string;
  descrizione: string;
  aliquota: number | string;
  predefinito?: boolean;
}

interface Fornitore {
  categoriaFornitore: string;
  iban?: string;
  abi?: string;
  cab?: string;
  banca?: string;
  filialeBanca?: string;
  condizionePagamentoId?: number;
  condizionePagamento?: CondizionePagamentoLite;
  codiceIvaDefaultId?: number;
  codiceIvaDefault?: { id: number; codice: string; descrizione: string; aliquota: number | string };
  note?: string;
}

interface Email {
  id: number;
  etichetta: string;
  email: string;
  principale: boolean;
  ambito?: 'GENERALE' | 'CLIENTE' | 'FORNITORE';
}

interface Contatto {
  id: number;
  nome: string;
  cognome?: string;
  ruolo?: string;
  telefono?: string;
  cellulare?: string;
  email?: string;
  principale: boolean;
  ambito?: 'GENERALE' | 'CLIENTE' | 'FORNITORE';
}

interface TabFornitoreProps {
  nominativoId: number;
  societaId: number;
  fornitore: Fornitore;
  email: Email[];
  contatti: Contatto[];
  onReload: () => void;
}

// ── Componente ────────────────────────────────────────────────────────────
export default function TabFornitore({ nominativoId, societaId, fornitore, email, contatti, onReload }: TabFornitoreProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    categoriaFornitore: fornitore.categoriaFornitore,
    iban: fornitore.iban || '',
    abi: fornitore.abi || '',
    cab: fornitore.cab || '',
    banca: fornitore.banca || '',
    filialeBanca: fornitore.filialeBanca || '',
    condizionePagamentoId: fornitore.condizionePagamentoId ?? null,
    codiceIvaDefaultId: fornitore.codiceIvaDefaultId ?? null,
    note: fornitore.note || '',
  });
  const [condizioniPagamento, setCondizioniPagamento] = useState<CondizionePagamentoLite[]>([]);
  const [codiciIva, setCodiciIva] = useState<CodiceIvaLite[]>([]);

  // ── Stato Email / Contatti ────────────────────────────────────────────────
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showContattoModal, setShowContattoModal] = useState(false);
  const [editingEmail, setEditingEmail] = useState<Email | null>(null);
  const [editingContatto, setEditingContatto] = useState<Contatto | null>(null);
  const [savingRelaz, setSavingRelaz] = useState(false);

  const [emailForm, setEmailForm] = useState({
    etichetta: '',
    email: '',
    principale: false,
  });

  const [contattoForm, setContattoForm] = useState({
    nome: '',
    cognome: '',
    ruolo: '',
    telefono: '',
    cellulare: '',
    email: '',
    principale: false,
  });

  const emailFornitore = email.filter(e => e.ambito === 'FORNITORE');
  const contattiFornitore = contatti.filter(c => c.ambito === 'FORNITORE');

  useEffect(() => {
    if (!showEditModal) return;
    (async () => {
      try {
        // @api GET /api/nominativi/opzioni/condizioni-pagamento?societaId=${societaId} -> {"success":"boolean","data":"CondizionePagamentoLite[]"}
        const res = await apiFetch<any>(`/api/nominativi/opzioni/condizioni-pagamento?societaId=${societaId}`);
        const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setCondizioniPagamento(arr);
      } catch (err) {
        console.error('Errore caricamento condizioni pagamento:', err);
        setCondizioniPagamento([]);
      }
      try {
        // @api GET /api/nominativi/opzioni/codici-iva?societaId=${societaId} -> {"success":"boolean","data":"CodiceIvaLite[]"}
        const res = await apiFetch<any>(`/api/nominativi/opzioni/codici-iva?societaId=${societaId}`);
        const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setCodiciIva(arr);
      } catch (err) {
        console.error('Errore caricamento codici IVA:', err);
        setCodiciIva([]);
      }
    })();
  }, [showEditModal, societaId]);

  const getCategoriaLabel = (categoria: string) => {
    switch (categoria) {
      case 'FORNITORE_MERCE': return 'Fornitore Merce';
      case 'SUBAPPALTATORE': return 'Subappaltatore';
      case 'FORNITORE_SERVIZI': return 'Fornitore Servizi';
      case 'AGENTE': return 'Agente';
      default: return categoria;
    }
  };

  const getCategoriaBadgeClass = (categoria: string): string => {
    switch (categoria) {
      case 'FORNITORE_MERCE':
        return 'bop-tab-fornitore__badge--merce';
      case 'SUBAPPALTATORE':
        return 'bop-tab-fornitore__badge--subappaltatore';
      case 'FORNITORE_SERVIZI':
        return 'bop-tab-fornitore__badge--servizi';
      case 'AGENTE':
        return 'bop-tab-fornitore__badge--agente';
      default:
        return 'bop-badge bop-badge--neutral';
    }
  };

  const formatIBAN = (iban: string) => {
    // Formatta IBAN con spazi ogni 4 caratteri
    return iban.replace(/(.{4})/g, '$1 ').trim();
  };

  const openEditModal = () => {
    setForm({
      categoriaFornitore: fornitore.categoriaFornitore,
      iban: fornitore.iban || '',
      abi: fornitore.abi || '',
      cab: fornitore.cab || '',
      banca: fornitore.banca || '',
      filialeBanca: fornitore.filialeBanca || '',
      condizionePagamentoId: fornitore.condizionePagamentoId ?? null,
      codiceIvaDefaultId: fornitore.codiceIvaDefaultId ?? null,
      note: fornitore.note || '',
    });
    setShowEditModal(true);
  };

  // ── Handlers Email ────────────────────────────────────────────────────────
  const openEmailModal = (em?: Email) => {
    if (em) {
      setEditingEmail(em);
      setEmailForm({ etichetta: em.etichetta, email: em.email, principale: em.principale });
    } else {
      setEditingEmail(null);
      setEmailForm({ etichetta: '', email: '', principale: false });
    }
    setShowEmailModal(true);
  };

  const saveEmail = async () => {
    setSavingRelaz(true);
    try {
      const payload = { ...emailForm, ambito: 'FORNITORE' };
      if (editingEmail) {
        // @api PUT /api/nominativi/:id/email/:id {"etichetta":"string","email":"string","principale":"boolean","ambito":"string"} -> {"ok":"boolean"}
        await apiFetch(`/api/nominativi/${nominativoId}/email/${editingEmail.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        // @api POST /api/nominativi/:id/email {"etichetta":"string","email":"string","principale":"boolean","ambito":"string"} -> {"id":"number"}
        await apiFetch(`/api/nominativi/${nominativoId}/email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      setShowEmailModal(false);
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante il salvataggio');
    } finally {
      setSavingRelaz(false);
    }
  };

  const deleteEmail = async (id: number) => {
    if (!window.confirm('Eliminare questa email?')) return;
    try {
      // @api DELETE /api/nominativi/:id/email/:id -> {"ok":"boolean"}
      await apiFetch(`/api/nominativi/${nominativoId}/email/${id}`, { method: 'DELETE' });
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante l\'eliminazione');
    }
  };

  // ── Handlers Contatti ─────────────────────────────────────────────────────
  const openContattoModal = (c?: Contatto) => {
    if (c) {
      setEditingContatto(c);
      setContattoForm({
        nome: c.nome,
        cognome: c.cognome || '',
        ruolo: c.ruolo || '',
        telefono: c.telefono || '',
        cellulare: c.cellulare || '',
        email: c.email || '',
        principale: c.principale,
      });
    } else {
      setEditingContatto(null);
      setContattoForm({ nome: '', cognome: '', ruolo: '', telefono: '', cellulare: '', email: '', principale: false });
    }
    setShowContattoModal(true);
  };

  const saveContatto = async () => {
    setSavingRelaz(true);
    try {
      const payload = { ...contattoForm, ambito: 'FORNITORE' };
      if (editingContatto) {
        // @api PUT /api/nominativi/:id/contatti/:id {"nome":"string","cognome":"string","ruolo":"string","telefono":"string","cellulare":"string","email":"string","principale":"boolean","ambito":"string"} -> {"ok":"boolean"}
        await apiFetch(`/api/nominativi/${nominativoId}/contatti/${editingContatto.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        // @api POST /api/nominativi/:id/contatti {"nome":"string","cognome":"string","ruolo":"string","telefono":"string","cellulare":"string","email":"string","principale":"boolean","ambito":"string"} -> {"id":"number"}
        await apiFetch(`/api/nominativi/${nominativoId}/contatti`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      setShowContattoModal(false);
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante il salvataggio');
    } finally {
      setSavingRelaz(false);
    }
  };

  const deleteContatto = async (id: number) => {
    if (!window.confirm('Eliminare questo contatto?')) return;
    try {
      // @api DELETE /api/nominativi/:id/contatti/:id -> {"ok":"boolean"}
      await apiFetch(`/api/nominativi/${nominativoId}/contatti/${id}`, { method: 'DELETE' });
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante l\'eliminazione');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // @api PUT /api/nominativi/:id/fornitore {"categoriaFornitore":"string","iban":"string","abi":"string","cab":"string","banca":"string","filialeBanca":"string","condizionePagamentoId":"number","codiceIvaDefaultId":"number","note":"string"} -> {"ok":"boolean"}
      await apiFetch(`/api/nominativi/${nominativoId}/fornitore`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categoriaFornitore: form.categoriaFornitore,
          iban: form.iban?.trim() || null,
          abi: form.abi?.trim() || null,
          cab: form.cab?.trim() || null,
          banca: form.banca?.trim() || null,
          filialeBanca: form.filialeBanca?.trim() || null,
          condizionePagamentoId: form.condizionePagamentoId,
          codiceIvaDefaultId: form.codiceIvaDefaultId,
          note: form.note || null,
        }),
      });
      setShowEditModal(false);
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Informazioni Fornitore */}
      <div className="bop-tab-fornitore__card">
        <h3 className="bop-tab-fornitore__section-title">
          Dati Fornitore
          <button className="bop-btn bop-btn-primary" onClick={openEditModal}>
            <EditIcon />
            Modifica
          </button>
        </h3>

        <div className="bop-tab-fornitore__field-grid">
          {/* Categoria Fornitore */}
          <div>
            <div className="bop-tab-fornitore__field-label">Categoria Fornitore</div>
            <div className="bop-tab-fornitore__field-value">
              <span className={`bop-tab-fornitore__badge ${getCategoriaBadgeClass(fornitore.categoriaFornitore)}`}>
                {getCategoriaLabel(fornitore.categoriaFornitore)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Sottoconto contabile (dinamico) */}
      <SottocontoCogeInfo nominativoId={nominativoId} tipo="FORNITORE" categoria={fornitore.categoriaFornitore} />

      {/* Dati Bancari e Pagamento */}
      <div className="bop-tab-fornitore__card">
        <h3 className="bop-tab-fornitore__section-title">
          Dati Bancari e Pagamento
        </h3>

        <div className="bop-tab-fornitore__field-grid">
          {/* IBAN */}
          <div className="bop-tab-fornitore__field-full">
            <div className="bop-tab-fornitore__field-label">IBAN</div>
            <div className="bop-tab-fornitore__field-value">
              {fornitore.iban ? (
                <div className="bop-tab-fornitore__iban">{formatIBAN(fornitore.iban)}</div>
              ) : (
                <span className="bop-tab-fornitore__empty-text">IBAN non registrato</span>
              )}
            </div>
          </div>

          {/* ABI / CAB */}
          <div>
            <div className="bop-tab-fornitore__field-label">ABI / CAB</div>
            <div className="bop-tab-fornitore__field-value">
              {fornitore.abi || fornitore.cab
                ? `${fornitore.abi || '—'} / ${fornitore.cab || '—'}`
                : <span className="bop-tab-fornitore__empty-text">Non specificati</span>}
            </div>
          </div>

          {/* Banca */}
          <div>
            <div className="bop-tab-fornitore__field-label">Banca</div>
            <div className="bop-tab-fornitore__field-value">
              {fornitore.banca || <span className="bop-tab-fornitore__empty-text">Non specificata</span>}
            </div>
          </div>

          {/* Filiale Banca */}
          <div>
            <div className="bop-tab-fornitore__field-label">Filiale</div>
            <div className="bop-tab-fornitore__field-value">
              {fornitore.filialeBanca || <span className="bop-tab-fornitore__empty-text">Non specificata</span>}
            </div>
          </div>

          {/* Condizione di pagamento */}
          <div>
            <div className="bop-tab-fornitore__field-label">Condizione di pagamento</div>
            <div className="bop-tab-fornitore__field-value">
              {fornitore.condizionePagamento
                ? fornitore.condizionePagamento.nome
                : <span className="bop-tab-fornitore__empty-text">Nessuna condizione impostata</span>}
            </div>
          </div>

          {/* Codice IVA di default */}
          <div>
            <div className="bop-tab-fornitore__field-label">Codice IVA di default</div>
            <div className="bop-tab-fornitore__field-value">
              {fornitore.codiceIvaDefault
                ? `${fornitore.codiceIvaDefault.codice} — ${fornitore.codiceIvaDefault.descrizione}`
                : <span className="bop-tab-fornitore__empty-text">Nessun codice IVA impostato</span>}
            </div>
          </div>
        </div>

        {(!fornitore.iban && !fornitore.banca) && (
          <div className="bop-tab-fornitore__info-box">
            <p className="bop-tab-fornitore__info-text">
              ⚠️ Nessun dato bancario registrato per questo fornitore.
            </p>
          </div>
        )}
      </div>

      {/* Note */}
      {fornitore.note && (
        <div className="bop-tab-fornitore__card">
          <h3 className="bop-tab-fornitore__section-title">
            Note Fornitore
          </h3>
          <div className="bop-tab-fornitore__info-box bop-tab-fornitore__info-box--prewrap">
            {fornitore.note}
          </div>
        </div>
      )}

      {/* Informazioni Categoria Specifica */}
      {fornitore.categoriaFornitore === 'SUBAPPALTATORE' && (
        <div className="bop-tab-fornitore__card">
          <h3 className="bop-tab-fornitore__section-title">
            Gestione Subappaltatore
          </h3>
          <div className="bop-tab-fornitore__info-box">
            <p className="bop-tab-fornitore__info-text">
              Per i fornitori <strong>Subappaltatore</strong>, è possibile associare commesse
              e gestire ordini specifici nella sezione Commesse.
            </p>
          </div>
        </div>
      )}

      {fornitore.categoriaFornitore === 'AGENTE' && (
        <div className="bop-tab-fornitore__card">
          <h3 className="bop-tab-fornitore__section-title">
            Informazioni Agente
          </h3>
          <div className="bop-tab-fornitore__info-box">
            <p className="bop-tab-fornitore__info-text">
              Questo nominativo è categorizzato come <strong>Agente</strong>. Può essere assegnato
              ad altri clienti come agente di riferimento e ricevere provvigioni.
            </p>
          </div>
        </div>
      )}

      {/* Storico Ordini */}
      <div className="bop-tab-fornitore__card">
        <h3 className="bop-tab-fornitore__section-title">
          Storico Ordini e Statistiche
        </h3>
        <div className="bop-tab-fornitore__info-box">
          <p className="bop-tab-fornitore__info-text">
            <strong>TODO:</strong> Qui verranno visualizzati:
          </p>
          <ul className="bop-tab-fornitore__info-list">
            <li>Totale ordini effettuati</li>
            <li>Importo totale fatturato</li>
            <li>Media tempi di consegna</li>
            <li>Ordini in corso / evasi</li>
            <li>Stato pagamenti verso fornitore</li>
            <li>Valutazione fornitore (se implementata)</li>
          </ul>
        </div>
      </div>

      {/* Modal Modifica */}
      {showEditModal && (
        <div className="bop-modal-overlay" onClick={() => !saving && setShowEditModal(false)}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Modifica Dati Fornitore</h3>
            </div>
            <div className="bop-modal__body">
              <div className="bop-form-group">
                <label className="bop-label">Categoria Fornitore *</label>
                <select
                  className="bop-select"
                  value={form.categoriaFornitore}
                  onChange={(e) => setForm({ ...form, categoriaFornitore: e.target.value })}
                  disabled={saving}
                >
                  <option value="FORNITORE_MERCE">Fornitore Merce</option>
                  <option value="SUBAPPALTATORE">Subappaltatore</option>
                  <option value="FORNITORE_SERVIZI">Fornitore Servizi</option>
                  <option value="AGENTE">Agente</option>
                </select>
              </div>

              <BancaFormFields
                value={{ iban: form.iban, abi: form.abi, cab: form.cab, banca: form.banca, filialeBanca: form.filialeBanca }}
                onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
                disabled={saving}
              />

              <div className="bop-form-group">
                <label className="bop-label">Condizione di pagamento</label>
                <select
                  className="bop-select"
                  value={form.condizionePagamentoId ?? ''}
                  onChange={(e) => setForm({
                    ...form,
                    condizionePagamentoId: e.target.value ? parseInt(e.target.value, 10) : null
                  })}
                  disabled={saving}
                >
                  <option value="">— Nessuna —</option>
                  {condizioniPagamento.map(c => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Codice IVA di default</label>
                <select
                  className="bop-select"
                  value={form.codiceIvaDefaultId ?? ''}
                  onChange={(e) => setForm({
                    ...form,
                    codiceIvaDefaultId: e.target.value ? parseInt(e.target.value, 10) : null
                  })}
                  disabled={saving}
                >
                  <option value="">— Nessuno —</option>
                  {codiciIva.map(c => (
                    <option key={c.id} value={c.id}>{c.codice} — {c.descrizione}</option>
                  ))}
                </select>
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Note</label>
                <textarea
                  className="bop-input bop-tab-fornitore__textarea"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Note aggiuntive sul fornitore..."
                  disabled={saving}
                />
              </div>
            </div>

            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setShowEditModal(false)} disabled={saving}>
                Annulla
              </button>
              <button className="bop-btn bop-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Fornitore */}
      <div className="bop-card bop-card__body">
        <h3 className="bop-anagrafica__section-title">
          Email
          <button className="bop-btn-primary" onClick={() => openEmailModal()}>
            <PlusIcon />
            Aggiungi
          </button>
        </h3>
        {emailFornitore.length > 0 ? (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Etichetta</th>
                <th>Email</th>
                <th className="bop-text-center">Principale</th>
                <th className="bop-text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {emailFornitore.map((em) => (
                <tr key={em.id}>
                  <td>{em.etichetta}</td>
                  <td className="bop-text-muted">{em.email}</td>
                  <td className="bop-text-center">
                    {em.principale && <span className="bop-badge bop-badge--success">Principale</span>}
                  </td>
                  <td className="bop-text-center">
                    <div className="bop-anagrafica__actions-cell">
                      <button className="bop-btn-secondary" onClick={() => openEmailModal(em)}>
                        <EditIcon />
                      </button>
                      <button className="bop-anagrafica__btn-delete" onClick={() => deleteEmail(em.id)}>
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="bop-empty-state">Nessuna email registrata per il fornitore</div>
        )}
      </div>

      {/* Contatti Fornitore */}
      <div className="bop-card bop-card__body">
        <h3 className="bop-anagrafica__section-title">
          Contatti
          <button className="bop-btn-primary" onClick={() => openContattoModal()}>
            <PlusIcon />
            Aggiungi
          </button>
        </h3>
        {contattiFornitore.length > 0 ? (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Ruolo</th>
                <th>Telefono</th>
                <th>Email</th>
                <th className="bop-text-center">Principale</th>
                <th className="bop-text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {contattiFornitore.map((contatto) => (
                <tr key={contatto.id}>
                  <td>{`${contatto.nome} ${contatto.cognome || ''}`.trim()}</td>
                  <td className="bop-text-muted">{contatto.ruolo || '—'}</td>
                  <td className="bop-text-muted">{contatto.telefono || contatto.cellulare || '—'}</td>
                  <td className="bop-text-muted">{contatto.email || '—'}</td>
                  <td className="bop-text-center">
                    {contatto.principale && <span className="bop-badge bop-badge--success">Principale</span>}
                  </td>
                  <td className="bop-text-center">
                    <div className="bop-anagrafica__actions-cell">
                      <button className="bop-btn-secondary" onClick={() => openContattoModal(contatto)}>
                        <EditIcon />
                      </button>
                      <button className="bop-anagrafica__btn-delete" onClick={() => deleteContatto(contatto.id)}>
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="bop-empty-state">Nessun contatto registrato per il fornitore</div>
        )}
      </div>

      {/* Modal Email */}
      {showEmailModal && (
        <div className="bop-modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">{editingEmail ? 'Modifica Email' : 'Nuova Email'}</h3>
            </div>
            <div className="bop-modal__body">
              <div className="bop-form-group">
                <label className="bop-label">Etichetta *</label>
                <input className="bop-input" value={emailForm.etichetta} onChange={(e) => setEmailForm({ ...emailForm, etichetta: e.target.value })} placeholder="es. Ufficio, Commerciale, ..." />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Email *</label>
                <input type="email" className="bop-input" value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} />
              </div>
              <div className="bop-form-group">
                <label className="bop-anagrafica__checkbox-label">
                  <input type="checkbox" checked={emailForm.principale} onChange={(e) => setEmailForm({ ...emailForm, principale: e.target.checked })} />
                  Email principale
                </label>
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn-secondary" onClick={() => setShowEmailModal(false)} disabled={savingRelaz}>Annulla</button>
              <button className="bop-btn-primary" onClick={saveEmail} disabled={savingRelaz}>
                {savingRelaz ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Contatto */}
      {showContattoModal && (
        <div className="bop-modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">{editingContatto ? 'Modifica Contatto' : 'Nuovo Contatto'}</h3>
            </div>
            <div className="bop-modal__body">
              <div className="bop-anagrafica__form-grid-2">
                <div className="bop-form-group">
                  <label className="bop-label">Nome *</label>
                  <input className="bop-input" value={contattoForm.nome} onChange={(e) => setContattoForm({ ...contattoForm, nome: e.target.value })} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Cognome</label>
                  <input className="bop-input" value={contattoForm.cognome} onChange={(e) => setContattoForm({ ...contattoForm, cognome: e.target.value })} />
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Ruolo</label>
                <input className="bop-input" value={contattoForm.ruolo} onChange={(e) => setContattoForm({ ...contattoForm, ruolo: e.target.value })} placeholder="es. Responsabile vendite" />
              </div>
              <div className="bop-anagrafica__form-grid-2">
                <div className="bop-form-group">
                  <label className="bop-label">Telefono</label>
                  <input className="bop-input" value={contattoForm.telefono} onChange={(e) => setContattoForm({ ...contattoForm, telefono: e.target.value })} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Cellulare</label>
                  <input className="bop-input" value={contattoForm.cellulare} onChange={(e) => setContattoForm({ ...contattoForm, cellulare: e.target.value })} />
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Email</label>
                <input type="email" className="bop-input" value={contattoForm.email} onChange={(e) => setContattoForm({ ...contattoForm, email: e.target.value })} />
              </div>
              <div className="bop-form-group">
                <label className="bop-anagrafica__checkbox-label">
                  <input type="checkbox" checked={contattoForm.principale} onChange={(e) => setContattoForm({ ...contattoForm, principale: e.target.checked })} />
                  Contatto principale
                </label>
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn-secondary" onClick={() => setShowContattoModal(false)} disabled={savingRelaz}>Annulla</button>
              <button className="bop-btn-primary" onClick={saveContatto} disabled={savingRelaz}>
                {savingRelaz ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </div>
  );
}
